import { useState } from 'react';
import type { AssignProgramInput } from '@st/contract';
import { useTrainerClients } from '../../../api/queries';
import { useTranslation } from '../../../i18n/I18nProvider';
import { todayIso } from '../../../lib/format';
import { Button, Input, Modal, Select, Switch } from '../../../ui';

export interface AssignProgramModalProps {
  open: boolean;
  programName: string;
  pending: boolean;
  onClose: () => void;
  onSubmit: (input: AssignProgramInput) => void;
}

/**
 * Dodjela traži i klijenta i datum početka — bez datuma se ne može izračunati
 * raspored ni definirati propušten trening, pa se prazna vrijednost odbija ovdje.
 */
export function AssignProgramModal({
  open,
  programName,
  pending,
  onClose,
  onSubmit,
}: AssignProgramModalProps) {
  const { t } = useTranslation();
  const clients = useTrainerClients();

  const [clientId, setClientId] = useState('');
  const [startDate, setStartDate] = useState(todayIso());
  const [replaceActive, setReplaceActive] = useState(true);
  const [errors, setErrors] = useState<{ client?: string; date?: string }>({});

  function submit() {
    const next: { client?: string; date?: string } = {};
    if (!clientId) next.client = t('builder.clientRequired');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate)) next.date = t('builder.startDateRequired');
    setErrors(next);
    if (next.client || next.date) return;
    onSubmit({ clientId, startDate, replaceActive });
  }

  const options = [
    { value: '', label: t('builder.chooseClient') },
    ...(clients.data?.items ?? []).map((c) => ({ value: c.id, label: `${c.name} · ${c.email}` })),
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('builder.assignTitle')}
      closeLabel={t('a11y.closeDialog')}
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" loading={pending} onClick={submit}>
            {t('builder.assignSubmit')}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <p className="text-[14.5px] text-ink-muted">{programName}</p>

        <Select
          id="assign-client"
          label={t('builder.assignClient')}
          value={clientId}
          required
          error={errors.client}
          options={options}
          onChange={(e) => setClientId(e.target.value)}
        />

        <Input
          id="assign-start"
          type="date"
          label={t('builder.assignStartDate')}
          value={startDate}
          required
          error={errors.date}
          onChange={(e) => setStartDate(e.target.value)}
        />

        <Switch
          id="assign-replace"
          checked={replaceActive}
          onCheckedChange={setReplaceActive}
          label={t('builder.assignReplace')}
          description={t('builder.assignReplaceHelp')}
        />
      </form>
    </Modal>
  );
}
